import React, { useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Sideways rows move like slides: the chevron sits beside the track,
 * and a tap brings the next full set of options into view.
 * A downward cue is only used when the row itself scrolls vertically.
 */
const ScrollCue = ({ axis = "x", className = "", children }) => {
    const ref = useRef(null);
    const [edges, setEdges] = useState({ start: false, end: false });

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        const measure = () => {
            const max = axis === "y" ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth;
            const pos = axis === "y" ? el.scrollTop : el.scrollLeft;
            const start = pos > 6;
            const end = max - pos > 6;
            setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
        };

        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        Array.from(el.children).forEach((child) => ro.observe(child));
        el.addEventListener("scroll", measure, { passive: true });
        window.addEventListener("resize", measure);
        return () => {
            ro.disconnect();
            el.removeEventListener("scroll", measure);
            window.removeEventListener("resize", measure);
        };
    });

    const itemOffsets = (el) => {
        const base = el.getBoundingClientRect().left;
        return Array.from(el.children).map((child) => ({
            left: child.getBoundingClientRect().left - base + el.scrollLeft,
            right: child.getBoundingClientRect().right - base + el.scrollLeft,
        }));
    };

    const slide = (dir) => {
        const el = ref.current;
        if (!el) return;
        if (axis === "y") {
            const amount = Math.max(140, el.clientHeight * 0.75);
            el.scrollBy({ top: dir * amount, behavior: "smooth" });
            return;
        }
        const items = itemOffsets(el);
        if (!items.length) return;
        if (dir > 0) {
            const viewEnd = el.scrollLeft + el.clientWidth;
            const next = items.find((item) => item.right > viewEnd + 1);
            el.scrollTo({ left: next ? next.left : el.scrollWidth, behavior: "smooth" });
            return;
        }
        const backTo = el.scrollLeft - el.clientWidth;
        const prev = [...items].reverse().find((item) => item.left <= backTo + 1);
        el.scrollTo({ left: prev ? prev.left : 0, behavior: "smooth" });
    };

    const arrowClass = "shrink-0 inline-flex items-center justify-center h-9 w-7 text-[#E31837]";

    if (axis === "y") {
        return (
            <div className="relative min-w-0">
                <div ref={ref} className={`no-scrollbar overflow-y-auto overscroll-contain ${className}`}>
                    {children}
                </div>
                {edges.end && (
                    <>
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent" />
                        <button type="button" aria-label="Show more options" onClick={() => slide(1)} className="absolute z-10 bottom-1 right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white text-[#E31837] border border-[#e8eaee] shadow-[0_2px_8px_rgba(10,37,64,0.18)]">
                            <ChevronDown size={16} />
                        </button>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="relative min-w-0 flex-1 flex items-center">
            {edges.start && (
                <button type="button" aria-label="Show previous options" onClick={() => slide(-1)} className={arrowClass}>
                    <ChevronLeft size={18} />
                </button>
            )}
            <div
                ref={ref}
                className={`no-scrollbar min-w-0 flex-1 overflow-x-auto snap-x snap-mandatory scroll-smooth [&>*]:snap-start ${className}`}
            >
                {children}
            </div>
            {edges.end && (
                <button type="button" aria-label="Show more options" onClick={() => slide(1)} className={arrowClass}>
                    <ChevronRight size={18} />
                </button>
            )}
        </div>
    );
};

export default ScrollCue;
